import { icons } from "./icons.js";
import { coverTransform, displayRectToNormalizedRoi, videoPointToDisplay } from "./geometry.js";
import type { NormalizedRoi } from "../pipeline/ScanPipeline.js";
import type { FrameQuality, ScanResult } from "../types.js";
import { formatById } from "../formats.js";

const TEMPLATE = /* html */ `
<div class="root">
  <video class="video" playsinline muted autoplay></video>
  <canvas class="overlay"></canvas>
  <div class="mask">
    <div class="mask-piece top"></div>
    <div class="mask-row">
      <div class="mask-piece side"></div>
      <div class="finder-hole"></div>
      <div class="mask-piece side"></div>
    </div>
    <div class="mask-piece bottom"></div>
  </div>
  <div class="finder" part="finder">
    <span class="corner tl"></span>
    <span class="corner tr"></span>
    <span class="corner bl"></span>
    <span class="corner br"></span>
  </div>
  <div class="topbar">
    <button class="icon-btn" data-action="home" aria-label="Home">${icons.home}</button>
    <div class="spacer"></div>
    <button class="icon-btn" data-action="switch-camera" aria-label="Switch camera">${icons.camera}</button>
    <button class="icon-btn" data-action="settings" aria-label="Settings">${icons.sliders}</button>
  </div>
  <button class="torch-btn" data-action="torch" aria-label="Toggle torch" hidden>${icons.torchOff}</button>
  <p class="hint">Move the finder over a barcode</p>
  <div class="quality-pill" hidden></div>
  <div class="top-notice" hidden></div>
  <div class="menu-scrim" hidden></div>
  <div class="camera-menu" hidden></div>
  <div class="toast" hidden>
    <div class="toast-format"></div>
    <div class="toast-text"></div>
  </div>
  <div class="batch-pill" hidden></div>
  <label class="upload-btn" data-action="upload" title="Scan from image file">
    ${icons.upload}
    <input type="file" accept="image/*" hidden />
  </label>
</div>
`;

const STYLE = /* css */ `
:host {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 320px;
  background: #05070a;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --jsscan-accent: #d81f4b;
  --jsscan-radius: 22px;
}
.root { position: absolute; inset: 0; }
.video {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; background: #000;
}
.overlay {
  position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none;
}
.mask {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  pointer-events: none;
}
.mask-piece { background: rgba(0,0,0,0.38); }
.mask.hidden .mask-piece { background: transparent; }
.mask-piece.top { flex: 0 0 28%; }
.mask-piece.bottom { flex: 1; }
.mask-row { display: flex; flex: 0 0 44%; }
.mask-piece.side { flex: 1; }
.finder-hole { flex: 0 0 80%; }
.finder {
  position: absolute; left: 10%; top: 28%; width: 80%; height: 44%;
  pointer-events: none;
}
.corner {
  position: absolute; width: 28px; height: 28px;
  border: 3px solid #fff;
  filter: drop-shadow(0 0 3px rgba(0,0,0,0.5));
}
.corner.tl { top: 0; left: 0; border-right: none; border-bottom: none; border-top-left-radius: 10px; }
.corner.tr { top: 0; right: 0; border-left: none; border-bottom: none; border-top-right-radius: 10px; }
.corner.bl { bottom: 0; left: 0; border-right: none; border-top: none; border-bottom-left-radius: 10px; }
.corner.br { bottom: 0; right: 0; border-left: none; border-top: none; border-bottom-right-radius: 10px; }
.finder.dim { opacity: 0; }
.topbar {
  position: absolute; top: 0; left: 0; right: 0;
  display: flex; align-items: center; gap: 10px;
  padding: max(14px, env(safe-area-inset-top)) 16px 14px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.55), transparent);
}
.spacer { flex: 1; }
.icon-btn, .torch-btn, .upload-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(20,20,24,0.55); color: #fff; border: none;
  cursor: pointer; backdrop-filter: blur(6px);
}
.icon-btn svg, .torch-btn svg, .upload-btn svg { width: 20px; height: 20px; }
.icon-btn:hover, .torch-btn:hover, .upload-btn:hover { background: rgba(40,40,46,0.75); }
.torch-btn {
  position: absolute; left: 50%; bottom: max(28px, env(safe-area-inset-bottom));
  transform: translateX(-50%);
}
.torch-btn.active { background: var(--jsscan-accent); }
.upload-btn { position: absolute; right: 16px; bottom: max(28px, env(safe-area-inset-bottom)); cursor: pointer; }
.hint {
  position: absolute; left: 0; right: 0; top: calc(28% + 44% + 18px);
  text-align: center; color: #fff; font-size: 14px; font-weight: 500;
  text-shadow: 0 1px 4px rgba(0,0,0,0.6); margin: 0; padding: 0 24px;
}
.quality-pill {
  position: absolute; left: 50%; top: calc(28% - 34px); transform: translateX(-50%);
  background: rgba(20,20,24,0.65); color: #fff; font-size: 11px; font-weight: 600;
  letter-spacing: 0.03em; padding: 5px 12px; border-radius: 999px; text-transform: uppercase;
}
.quality-pill[data-tone="warn"] { background: rgba(196, 130, 10, 0.85); }
.quality-pill[data-tone="bad"] { background: rgba(196, 40, 40, 0.85); }
.toast {
  position: absolute; left: 16px; right: 16px; bottom: max(24px, env(safe-area-inset-bottom));
  background: rgba(18,20,24,0.92); color: #fff; border-radius: 16px; padding: 14px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
}
.toast-format {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--jsscan-accent); font-weight: 700; margin-bottom: 4px;
}
.toast-text { font-size: 15px; font-weight: 500; word-break: break-all; }
.batch-pill {
  position: absolute; right: 16px; top: max(70px, calc(env(safe-area-inset-top) + 66px));
  background: var(--jsscan-accent); color: #fff; font-size: 12px; font-weight: 700;
  padding: 6px 12px; border-radius: 999px;
}
.top-notice {
  position: absolute; left: 50%; top: max(70px, calc(env(safe-area-inset-top) + 66px));
  transform: translateX(-50%);
  background: rgba(196, 40, 40, 0.92); color: #fff; font-size: 12.5px; font-weight: 600;
  padding: 7px 14px; border-radius: 999px; white-space: nowrap;
  box-shadow: 0 4px 14px rgba(0,0,0,0.3); z-index: 7;
}
.menu-scrim { position: absolute; inset: 0; z-index: 5; background: transparent; }
.camera-menu {
  position: absolute; top: max(60px, calc(env(safe-area-inset-top) + 56px)); right: 60px;
  z-index: 6; min-width: 190px; max-width: 260px;
  background: rgba(24,24,28,0.96); border-radius: 12px; padding: 6px;
  box-shadow: 0 10px 28px rgba(0,0,0,0.45); backdrop-filter: blur(8px);
}
.camera-menu-item {
  display: block; width: 100%; text-align: left; box-sizing: border-box;
  padding: 9px 12px; border-radius: 8px; color: #fff; font-size: 13px;
  cursor: pointer; background: transparent; border: none;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.camera-menu-item:hover { background: rgba(255,255,255,0.1); }
.camera-menu-item.active { color: var(--jsscan-accent); font-weight: 700; }
.camera-menu-empty { padding: 9px 12px; color: rgba(255,255,255,0.6); font-size: 12.5px; }
`;

export interface QualityLabel {
  label: string;
  tone: "ok" | "warn" | "bad";
}

const QUALITY_LABELS: Record<FrameQuality["condition"], QualityLabel | null> = {
  good: null,
  "low-light": { label: "Low light", tone: "warn" },
  glare: { label: "Glare detected", tone: "warn" },
  blurry: { label: "Hold steady", tone: "warn" },
  mixed: { label: "Low light", tone: "warn" },
};

export class ScannerView extends HTMLElement {
  private readonly shadow: ShadowRoot;
  readonly videoEl: HTMLVideoElement;
  private readonly overlayEl: HTMLCanvasElement;
  private readonly overlayCtx: CanvasRenderingContext2D;
  private readonly maskEl: HTMLDivElement;
  private readonly finderEl: HTMLDivElement;
  private readonly hintEl: HTMLParagraphElement;
  private readonly torchBtn: HTMLButtonElement;
  private readonly toastEl: HTMLDivElement;
  private readonly batchPillEl: HTMLDivElement;
  private readonly qualityPillEl: HTMLDivElement;
  private readonly topNoticeEl: HTMLDivElement;
  private readonly menuScrimEl: HTMLDivElement;
  private readonly cameraMenuEl: HTMLDivElement;
  private readonly uploadInput: HTMLInputElement;
  private toastTimer = 0;
  private topNoticeTimer = 0;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLE;
    const wrapper = document.createElement("div");
    wrapper.innerHTML = TEMPLATE;
    this.shadow.append(style, wrapper.firstElementChild!);

    this.videoEl = this.shadow.querySelector(".video")!;
    this.overlayEl = this.shadow.querySelector(".overlay")!;
    this.overlayCtx = this.overlayEl.getContext("2d")!;
    this.maskEl = this.shadow.querySelector(".mask")!;
    this.finderEl = this.shadow.querySelector(".finder")!;
    this.hintEl = this.shadow.querySelector(".hint")!;
    this.torchBtn = this.shadow.querySelector(".torch-btn")!;
    this.toastEl = this.shadow.querySelector(".toast")!;
    this.batchPillEl = this.shadow.querySelector(".batch-pill")!;
    this.qualityPillEl = this.shadow.querySelector(".quality-pill")!;
    this.topNoticeEl = this.shadow.querySelector(".top-notice")!;
    this.menuScrimEl = this.shadow.querySelector(".menu-scrim")!;
    this.cameraMenuEl = this.shadow.querySelector(".camera-menu")!;
    this.uploadInput = this.shadow.querySelector(".upload-btn input")!;

    this.shadow.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((el) => {
      const action = el.dataset.action;
      if (action === "upload") return;
      el.addEventListener("click", () => this.dispatchEvent(new CustomEvent(action!)));
    });
    this.menuScrimEl.addEventListener("click", () => this.hideCameraPicker());
    this.uploadInput.addEventListener("change", () => {
      const file = this.uploadInput.files?.[0];
      if (file) this.dispatchEvent(new CustomEvent<File>("file-upload", { detail: file }));
      this.uploadInput.value = "";
    });

    new ResizeObserver(() => this.syncOverlaySize()).observe(this);
  }

  connectedCallback(): void {
    this.syncOverlaySize();
  }

  private syncOverlaySize(): void {
    const rect = this.getBoundingClientRect();
    this.overlayEl.width = Math.max(1, Math.round(rect.width * devicePixelRatio));
    this.overlayEl.height = Math.max(1, Math.round(rect.height * devicePixelRatio));
  }

  setHint(text: string): void {
    this.hintEl.textContent = text;
  }

  setViewFinderVisible(visible: boolean): void {
    this.finderEl.classList.toggle("dim", !visible);
    this.maskEl.classList.toggle("hidden", !visible);
  }

  setTorchAvailable(available: boolean): void {
    this.torchBtn.hidden = !available;
  }

  setTorchState(on: boolean): void {
    this.torchBtn.classList.toggle("active", on);
    this.torchBtn.innerHTML = on ? icons.torchOn : icons.torchOff;
  }

  /** Transient top-center banner for one-off notices (e.g. "flashlight not available"). */
  showTopNotice(text: string, autoHideMs = 2600): void {
    this.topNoticeEl.textContent = text;
    this.topNoticeEl.hidden = false;
    clearTimeout(this.topNoticeTimer);
    if (autoHideMs > 0) {
      this.topNoticeTimer = window.setTimeout(() => (this.topNoticeEl.hidden = true), autoHideMs);
    }
  }

  hideTopNotice(): void {
    this.topNoticeEl.hidden = true;
    clearTimeout(this.topNoticeTimer);
  }

  isCameraPickerOpen(): boolean {
    return !this.cameraMenuEl.hidden;
  }

  showCameraPicker(devices: { deviceId: string; label: string }[], activeDeviceId?: string | null): void {
    this.cameraMenuEl.innerHTML = "";
    if (devices.length === 0) {
      const empty = document.createElement("div");
      empty.className = "camera-menu-empty";
      empty.textContent = "No cameras found";
      this.cameraMenuEl.appendChild(empty);
    } else {
      devices.forEach((device, index) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "camera-menu-item" + (device.deviceId === activeDeviceId ? " active" : "");
        btn.textContent = device.label || `Camera ${index + 1}`;
        btn.addEventListener("click", () => {
          this.hideCameraPicker();
          this.dispatchEvent(new CustomEvent<string>("select-camera", { detail: device.deviceId }));
        });
        this.cameraMenuEl.appendChild(btn);
      });
    }
    this.menuScrimEl.hidden = false;
    this.cameraMenuEl.hidden = false;
  }

  hideCameraPicker(): void {
    this.menuScrimEl.hidden = true;
    this.cameraMenuEl.hidden = true;
  }

  setFrameQuality(quality: FrameQuality | null): void {
    const info = quality ? QUALITY_LABELS[quality.condition] : null;
    if (!info) {
      this.qualityPillEl.hidden = true;
      return;
    }
    this.qualityPillEl.hidden = false;
    this.qualityPillEl.textContent = info.label;
    this.qualityPillEl.dataset.tone = info.tone;
  }

  showResultToast(result: ScanResult, autoHideMs = 2200): void {
    const label = formatById(result.format)?.label ?? result.format;
    this.toastEl.querySelector(".toast-format")!.textContent = label;
    this.toastEl.querySelector(".toast-text")!.textContent = result.text;
    this.toastEl.hidden = false;
    clearTimeout(this.toastTimer);
    if (autoHideMs > 0) {
      this.toastTimer = window.setTimeout(() => (this.toastEl.hidden = true), autoHideMs);
    }
  }

  showMessage(text: string, autoHideMs = 2200): void {
    this.toastEl.querySelector(".toast-format")!.textContent = "";
    this.toastEl.querySelector(".toast-text")!.textContent = text;
    this.toastEl.hidden = false;
    clearTimeout(this.toastTimer);
    if (autoHideMs > 0) {
      this.toastTimer = window.setTimeout(() => (this.toastEl.hidden = true), autoHideMs);
    }
  }

  hideResultToast(): void {
    this.toastEl.hidden = true;
    clearTimeout(this.toastTimer);
  }

  setBatchCount(count: number): void {
    if (count <= 0) {
      this.batchPillEl.hidden = true;
      return;
    }
    this.batchPillEl.hidden = false;
    this.batchPillEl.textContent = `${count} scanned`;
  }

  /** Draw live bounding boxes for the current frame's results (AR overlay). */
  drawOverlay(results: ScanResult[]): void {
    const ctx = this.overlayCtx;
    const { width, height } = this.overlayEl;
    ctx.clearRect(0, 0, width, height);
    if (results.length === 0) return;
    const rect = this.getBoundingClientRect();
    const dpr = devicePixelRatio;
    const t = coverTransform(this.videoEl.videoWidth, this.videoEl.videoHeight, rect.width, rect.height);

    ctx.lineWidth = 3 * dpr;
    ctx.strokeStyle = "#2fd67a";
    ctx.fillStyle = "rgba(47, 214, 122, 0.18)";
    for (const r of results) {
      const pts = [r.position.topLeft, r.position.topRight, r.position.bottomRight, r.position.bottomLeft].map((p) => {
        const d = videoPointToDisplay(p, t);
        return { x: d.x * dpr, y: d.y * dpr };
      });
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (const p of pts.slice(1)) ctx.lineTo(p.x, p.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }

  clearOverlay(): void {
    this.overlayCtx.clearRect(0, 0, this.overlayEl.width, this.overlayEl.height);
  }

  /** Normalized (0..1) ROI of the finder rectangle in *video intrinsic pixel* space. */
  computeRoi(): NormalizedRoi | null {
    const vw = this.videoEl.videoWidth;
    const vh = this.videoEl.videoHeight;
    if (!vw || !vh) return null;
    const containerRect = this.getBoundingClientRect();
    const finderRect = this.finderEl.getBoundingClientRect();
    return displayRectToNormalizedRoi(
      {
        x: finderRect.x - containerRect.x,
        y: finderRect.y - containerRect.y,
        width: finderRect.width,
        height: finderRect.height,
      },
      vw,
      vh,
      containerRect.width,
      containerRect.height,
    );
  }
}

customElements.define("jsscan-view", ScannerView);
